import { useEffect, useMemo, useRef, useState } from "react";
import { http, API } from "../api";
import {
  X, Phone, MessageCircle, Compass, AlertTriangle, CalendarDays, Vote,
  MessageSquare, Heart, Building2, StickyNote, Trash2, Send, Printer,
  Star, TrendingUp, Clock, Users, Network, Search, Sparkles, Link2, Check, Plus, QrCode, Download,
  BarChart3, Flame, ArrowLeft, Package, CheckCircle2, Languages,
} from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import { openPerson } from "../shared";

const STAGE_ORDER = ["Unknown", "Contacted", "Engaged", "Supporter", "Volunteer"];
const STAGE_COLOR = {
  Unknown: "#c8d3ce", Contacted: "#7ab6c5", Engaged: "#efb64e",
  Supporter: "#65aa79", Volunteer: "#0f766e",
};

const TYPE_META = {
  complaint: { icon: AlertTriangle, label: "Complaints", tone: "red" },
  field_visit: { icon: Compass, label: "Visits", tone: "blue" },
  event_rsvp: { icon: CalendarDays, label: "Events", tone: "amber" },
  turnout: { icon: Vote, label: "Turnout", tone: "green" },
  chat_mention: { icon: MessageSquare, label: "Chats", tone: "blue" },
  donation: { icon: Heart, label: "Donations", tone: "teal" },
  org_membership: { icon: Building2, label: "Memberships", tone: "teal" },
};

export default function VoterJourney({ contactId, user, onClose, inline }) {
  const [data, setData] = useState(null); // { contact, timeline, notes, family_members, household, stats }
  const [graph, setGraph] = useState(null);
  const [loadingGraph, setLoadingGraph] = useState(false);
  const [tab, setTab] = useState("timeline"); // timeline | notes | graph
  const [typeFilter, setTypeFilter] = useState("all");
  const [q, setQ] = useState("");
  const [noteText, setNoteText] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [templates, setTemplates] = useState([]);
  const [manageTpl, setManageTpl] = useState(false);
  const [tplView, setTplView] = useState("list"); // list | analytics | presets
  const [tplAnalytics, setTplAnalytics] = useState(null);
  const [loadingAnalytics, setLoadingAnalytics] = useState(false);
  const [presetPacks, setPresetPacks] = useState([]);
  const [importingPack, setImportingPack] = useState(null);
  const [importResult, setImportResult] = useState(null);
  const [newTpl, setNewTpl] = useState("");
  const [newTplCat, setNewTplCat] = useState("General");
  const [copiedLink, setCopiedLink] = useState(false);
  const [showQr, setShowQr] = useState(false);
  const isAdmin = user && (user.role === "Super Admin" || user.role === "Admin");

  const reload = () => http.get(`/voters/${contactId}/timeline`).then((r) => setData(r.data));
  const loadTemplates = () => http.get("/voter-note-templates").then((r) => setTemplates(r.data)).catch(() => {});

  useEffect(() => { setData(null); setGraph(null); setTab("timeline"); setTypeFilter("all"); setQ(""); }, [contactId]);
  useEffect(() => { if (contactId) reload(); }, [contactId]);
  useEffect(() => { loadTemplates(); }, []);
  useEffect(() => {
    if (tab === "graph" && contactId && !graph) {
      setLoadingGraph(true);
      http.get(`/voters/${contactId}/graph`)
        .then((r) => setGraph(r.data))
        .finally(() => setLoadingGraph(false));
    }
  }, [tab, contactId, graph]);

  // Escape closes
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose?.(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const addNote = async () => {
    if (!noteText.trim()) return;
    setBusy(true); setErr("");
    try {
      await http.post(`/voters/${contactId}/notes`, { text: noteText.trim() });
      setNoteText("");
      await reload();
    } catch (e) {
      setErr(e.response?.data?.detail || "Could not save note");
    } finally { setBusy(false); }
  };

  const deleteNote = async (noteId) => {
    if (!window.confirm("Delete this note?")) return;
    await http.delete(`/voters/${contactId}/notes/${noteId}`);
    await reload();
  };

  const copyJourneyLink = async () => {
    const url = `${window.location.origin}/journey/${contactId}`;
    try {
      if (navigator.clipboard?.writeText) await navigator.clipboard.writeText(url);
      else {
        const ta = document.createElement("textarea");
        ta.value = url; ta.style.position = "fixed"; ta.style.opacity = "0";
        document.body.appendChild(ta); ta.select(); document.execCommand("copy"); document.body.removeChild(ta);
      }
      setCopiedLink(true);
      setTimeout(() => setCopiedLink(false), 2000);
    } catch { /* noop */ }
  };

  const dropInTemplate = (t) => {
    setNoteText((cur) => cur ? `${cur}\n${t.text}` : t.text);
    // Fire-and-forget: bump the template's usage counter so it floats up next time
    http.post(`/voter-note-templates/${t.template_id}/hit`).catch(() => {});
  };

  const addTemplate = async () => {
    if (!newTpl.trim()) return;
    try {
      const r = await http.post("/voter-note-templates", { text: newTpl.trim(), category: newTplCat });
      setTemplates((cur) => [...cur, r.data]);
      setNewTpl("");
    } catch (e) { setErr(e.response?.data?.detail || "Could not save template"); }
  };

  const deleteTemplate = async (tid) => {
    if (!window.confirm("Delete this template?")) return;
    await http.delete(`/voter-note-templates/${tid}`);
    setTemplates((cur) => cur.filter((t) => t.template_id !== tid));
  };

  const loadAnalytics = async () => {
    setLoadingAnalytics(true);
    try {
      const r = await http.get("/voter-note-templates/analytics?months=6");
      setTplAnalytics(r.data);
    } catch (e) { /* noop */ }
    finally { setLoadingAnalytics(false); }
  };

  const openAnalytics = () => { setTplView("analytics"); if (!tplAnalytics) loadAnalytics(); };
  const openPresets = async () => {
    setTplView("presets"); setImportResult(null);
    if (presetPacks.length === 0) {
      try { const r = await http.get("/voter-note-templates/presets"); setPresetPacks(r.data.packs || []); } catch { /* noop */ }
    }
  };
  const importPack = async (pack) => {
    setImportingPack(pack.id); setImportResult(null);
    try {
      const r = await http.post(`/voter-note-templates/presets/${pack.id}/import`);
      setImportResult({ pack: pack.name, ...r.data });
      await loadTemplates();
    } catch (e) { setErr(e.response?.data?.detail || "Import failed"); }
    finally { setImportingPack(null); }
  };
  const openManageTpl = () => { setManageTpl(true); setTplView("list"); setImportResult(null); };

  const groupedTimeline = useMemo(() => {
    if (!data) return [];
    let items = data.timeline || [];
    if (typeFilter !== "all") items = items.filter((t) => t.type === typeFilter);
    if (q.trim()) {
      const s = q.toLowerCase();
      items = items.filter((t) => (t.title || "").toLowerCase().includes(s) || (t.meta || "").toLowerCase().includes(s));
    }
    // Group by YYYY-MM
    const groups = new Map();
    for (const t of items) {
      const key = (t.when || "").slice(0, 7) || "no-date";
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key).push(t);
    }
    return Array.from(groups.entries());
  }, [data, typeFilter, q]);

  if (!data) return (
    <div className="vj-shell" data-testid="voter-journey-shell">
      <div className="vj-close-row">{!inline && <button className="icon-btn" onClick={onClose} data-testid="vj-close"><X size={18} /></button>}</div>
      <p className="hint" style={{ padding: 40, textAlign: "center" }}>Loading voter profile…</p>
    </div>
  );

  const { contact, notes = [], family_members = [], household, stats = {} } = data;
  const stageIdx = STAGE_ORDER.indexOf(stats.stage || "Unknown");

  const waLink = contact.mobile ? `https://wa.me/${(contact.mobile || "").replace(/[^0-9]/g, "")}` : "";
  const telLink = contact.mobile ? `tel:${contact.mobile}` : "";

  return (
    <div className="vj-shell" data-testid="voter-journey-shell">
      {/* -------- HERO -------- */}
      <div className="vj-hero" data-testid="vj-hero">
        {contact.cover_photo ? (
          <img className="vj-cover" src={`${API}/files/${contact.cover_photo}`} alt="Cover" />
        ) : (
          <div className="vj-cover vj-cover--default" />
        )}
        {!inline && <button className="icon-btn vj-close" onClick={onClose} data-testid="vj-close"><X size={18} /></button>}
        <button className="icon-btn vj-print" onClick={() => window.print()} data-testid="vj-print" title="Print / Save as PDF"><Printer size={16} /></button>
        <button className="icon-btn vj-link" onClick={copyJourneyLink} data-testid="vj-copy-link" title="Copy journey deep-link">
          {copiedLink ? <Check size={16} /> : <Link2 size={16} />}
        </button>
        <button className="icon-btn vj-qr" onClick={() => setShowQr(true)} data-testid="vj-qr-btn" title="Show QR code for this voter"><QrCode size={16} /></button>
        <div className="vj-hero-body">
          <div className="vj-avatar">
            {contact.profile_photo
              ? <img src={`${API}/files/${contact.profile_photo}`} alt={contact.name} />
              : <span>{(contact.name || "?").split(" ").map((s) => s[0]).join("").slice(0, 2).toUpperCase()}</span>}
          </div>
          <div className="vj-hero-info">
            <span className="vj-eyebrow">WARD {contact.ward || 7} · BOOTH {contact.booth}</span>
            <h1 data-testid="vj-name">{contact.name}</h1>
            <p className="vj-tags">
              {contact.gender && <span>{contact.gender}</span>}
              {contact.age && <span>· {contact.age} yr</span>}
              {contact.profession && <span>· {contact.profession}</span>}
              {contact.blood_group && <span>· {contact.blood_group}</span>}
            </p>
            {contact.address && <p className="vj-address">{contact.address}</p>}
          </div>
          <div className="vj-quick-actions">
            {telLink && <a className="vj-qa" href={telLink} data-testid="vj-call"><Phone size={14} /> Call</a>}
            {waLink && <a className="vj-qa" href={waLink} target="_blank" rel="noopener noreferrer" data-testid="vj-whatsapp"><MessageCircle size={14} /> WhatsApp</a>}
          </div>
        </div>

        {/* Stage pipeline */}
        <div className="vj-pipeline" data-testid="vj-pipeline">
          {STAGE_ORDER.map((s, i) => (
            <div key={s} className={`vj-stage ${i <= stageIdx ? "reached" : ""} ${i === stageIdx ? "current" : ""}`} data-testid={`vj-stage-${s.toLowerCase()}`}>
              <span className="vj-stage-dot" style={{ background: i <= stageIdx ? STAGE_COLOR[s] : "#e6efe9" }} />
              <span className="vj-stage-lbl">{s}</span>
            </div>
          ))}
        </div>
      </div>

      {/* -------- BODY -------- */}
      <div className="vj-body">
        {/* Left column: stats + household + memberships */}
        <aside className="vj-side">
          <div className="vj-stat-grid" data-testid="vj-stats">
            <StatTile icon={Star} label="Engagement" value={`${stats.score || 0}`} suffix="/100" tone="teal" testid="vj-stat-score" />
            <StatTile icon={Clock} label="Last touch" value={stats.days_since_last === null || stats.days_since_last === undefined ? "—" : `${stats.days_since_last}d`} suffix={stats.days_since_last === null ? "" : " ago"} tone="amber" testid="vj-stat-last" />
            <StatTile icon={TrendingUp} label="Total touches" value={stats.touch_count || 0} tone="blue" testid="vj-stat-count" />
            <StatTile icon={Sparkles} label="Top type" value={stats.top_type ? (TYPE_META[stats.top_type]?.label || stats.top_type) : "—"} tone="teal" testid="vj-stat-top" />
          </div>

          {(household || family_members.length > 0) && (
            <div className="vj-panel" data-testid="vj-household">
              <p className="eyebrow"><Users size={12} /> HOUSEHOLD {household?.family_head ? `· ${household.family_head}` : ""}</p>
              {family_members.length === 0 ? (
                <p className="hint" style={{ margin: "6px 0 0" }}>No linked family members.</p>
              ) : (
                <div className="vj-family">
                  {family_members.map((m) => (
                    <div
                      key={m.contact_id}
                      className="vj-family-chip clickable"
                      data-testid={`vj-family-${m.contact_id}`}
                      onClick={() => openPerson(m.contact_id, m.name)}
                    >
                      <span className="vj-family-avatar">{(m.name || "?").split(" ").map((s) => s[0]).join("").slice(0, 2)}</span>
                      <div>
                        <b>{m.name}</b>
                        <small>{m.gender ? m.gender[0] : ""}{m.age ? ` · ${m.age}` : ""}{m.profession ? ` · ${m.profession}` : ""}</small>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {contact.inclination && (
            <div className="vj-panel">
              <p className="eyebrow">INCLINATION</p>
              <p className="vj-inclination">
                <i className={`status-dot ${contact.inclination === "Supporter" ? "green" : contact.inclination === "Neutral" ? "blue" : contact.inclination === "Opposition" ? "red" : "amber"}`} />
                {contact.inclination}
              </p>
            </div>
          )}
        </aside>

        {/* Right column: tabs -> timeline | notes | graph */}
        <main className="vj-main">
          <div className="vj-tabs" role="tablist">
            <button className={tab === "timeline" ? "active" : ""} onClick={() => setTab("timeline")} data-testid="vj-tab-timeline">Timeline ({stats.touch_count || 0})</button>
            <button className={tab === "notes" ? "active" : ""} onClick={() => setTab("notes")} data-testid="vj-tab-notes">Notes ({stats.note_count || 0})</button>
            <button className={tab === "graph" ? "active" : ""} onClick={() => setTab("graph")} data-testid="vj-tab-graph"><Network size={12} /> Network</button>
          </div>

          {tab === "timeline" && (
            <TimelineTab
              groupedTimeline={groupedTimeline}
              typeFilter={typeFilter} setTypeFilter={setTypeFilter}
              q={q} setQ={setQ}
              typeCounts={stats.type_counts || {}}
            />
          )}

          {tab === "notes" && (
            <NotesTab
              notes={notes} isAdmin={isAdmin} user={user}
              noteText={noteText} setNoteText={setNoteText}
              onAdd={addNote} onDelete={deleteNote}
              busy={busy} err={err}
              templates={templates}
              onDropInTemplate={dropInTemplate}
              onOpenManageTpl={openManageTpl}
            />
          )}

          {tab === "graph" && (
            <GraphTab graph={graph} loading={loadingGraph} centerId={contactId} />
          )}
        </main>
      </div>

      {showQr && (
        <div className="vj-tpl-manager" data-testid="vj-qr-modal" onClick={(e) => { if (e.target === e.currentTarget) setShowQr(false); }}>
          <div className="vj-tpl-inner" style={{ maxWidth: 380, textAlign: "center" }}>
            <div className="vj-tpl-head">
              <h3>Scan to open journey</h3>
              <button className="icon-btn" onClick={() => setShowQr(false)} data-testid="vj-qr-close"><X size={16} /></button>
            </div>
            <p className="hint" style={{ marginBottom: 14 }}>Point a field volunteer&apos;s phone at this QR to jump straight to <b>{data.contact.name}</b>&apos;s journey.</p>
            <div style={{ display: "inline-block", background: "white", padding: 14, border: "1px solid var(--line)", borderRadius: 10 }} data-testid="vj-qr-svg">
              <QRCodeSVG value={`${window.location.origin}/journey/${contactId}`} size={220} bgColor="#ffffff" fgColor="#0f766e" level="M" />
            </div>
            <p className="hint" style={{ margin: "12px 0 0", wordBreak: "break-all" }}>{`${window.location.origin}/journey/${contactId}`}</p>
            <div style={{ display: "flex", gap: 8, justifyContent: "center", marginTop: 14 }}>
              <button className="outline" onClick={copyJourneyLink} data-testid="vj-qr-copy">{copiedLink ? <><Check size={13} /> Copied</> : <><Link2 size={13} /> Copy link</>}</button>
              <button className="outline" onClick={() => downloadJourneyQr(data.contact.name, contactId)} data-testid="vj-qr-download"><Download size={13} /> Download PNG</button>
            </div>
          </div>
        </div>
      )}

      {manageTpl && (
        <div className="vj-tpl-manager" data-testid="vj-tpl-manager" onClick={(e) => { if (e.target === e.currentTarget) setManageTpl(false); }}>
          <div className="vj-tpl-inner">
            <div className="vj-tpl-head">
              <h3>
                {tplView === "analytics" && (
                  <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
                    <button className="icon-btn" onClick={() => setTplView("list")} data-testid="vj-tpl-back"><ArrowLeft size={14} /></button>
                    Template analytics
                  </span>
                )}
                {tplView === "presets" && (
                  <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
                    <button className="icon-btn" onClick={() => setTplView("list")} data-testid="vj-tpl-back-presets"><ArrowLeft size={14} /></button>
                    Template packs
                  </span>
                )}
                {tplView === "list" && "Note templates"}
              </h3>
              <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                {tplView === "list" && (
                  <>
                    <button className="vj-tpl-analytics-open" onClick={openPresets} data-testid="vj-tpl-presets-btn" title="Import a starter pack">
                      <Package size={12} /> Packs
                    </button>
                    <button className="vj-tpl-analytics-open" onClick={openAnalytics} data-testid="vj-tpl-analytics-btn" title="View usage analytics">
                      <BarChart3 size={12} /> Analytics
                    </button>
                  </>
                )}
                <button className="icon-btn" onClick={() => setManageTpl(false)} data-testid="vj-tpl-close"><X size={16} /></button>
              </div>
            </div>

            {tplView === "list" && (
              <>
                <p className="hint">Save common note phrases your team drops in most days.</p>
                {isAdmin && (
                  <div className="vj-tpl-composer">
                    <input placeholder='e.g. "Attended Deepavali event", "Requested water tanker"'
                      value={newTpl} onChange={(e) => setNewTpl(e.target.value)}
                      onKeyDown={(e) => { if (e.key === "Enter") addTemplate(); }}
                      data-testid="vj-tpl-input" />
                    <select value={newTplCat} onChange={(e) => setNewTplCat(e.target.value)} data-testid="vj-tpl-cat-select">
                      {TPL_CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                    </select>
                    <button className="primary" onClick={addTemplate} disabled={!newTpl.trim()} data-testid="vj-tpl-add"><Plus size={13} /> Add</button>
                  </div>
                )}
                {templates.length === 0 ? (
                  <p className="hint" style={{ textAlign: "center", padding: 20 }}>No templates yet.</p>
                ) : (
                  <ul className="vj-tpl-list" data-testid="vj-tpl-list">
                    {templates.map((t) => {
                      const canDelete = user && (t.created_by_id === user.user_id || user.role === "Super Admin");
                      const c = TPL_CAT_COLOR[t.category] || TPL_CAT_COLOR.General;
                      return (
                        <li key={t.template_id} data-testid={`vj-tpl-item-${t.template_id}`}>
                          <span className="vj-tpl-cat" style={{ background: c.bg, color: c.fg, borderColor: c.border, marginRight: 8 }}>{t.category || "General"}</span>
                          <span>
                            {t.text}
                            <em className="vj-tpl-uses" data-testid={`vj-tpl-item-uses-${t.template_id}`}> · {t.use_count || 0} use{(t.use_count || 0) === 1 ? "" : "s"}</em>
                          </span>
                          <small>{t.created_by_name}</small>
                          {canDelete && <button className="icon-btn danger" onClick={() => deleteTemplate(t.template_id)} data-testid={`vj-tpl-del-${t.template_id}`}><Trash2 size={12} /></button>}
                        </li>
                      );
                    })}
                  </ul>
                )}
              </>
            )}

            {tplView === "analytics" && (
              <TemplateAnalyticsView data={tplAnalytics} loading={loadingAnalytics} />
            )}

            {tplView === "presets" && (
              <div data-testid="vj-tpl-presets">
                <p className="hint">Import a curated starter pack — dedupes existing templates automatically.</p>
                {importResult && (
                  <div className="vj-preset-toast" data-testid="vj-preset-import-result">
                    <CheckCircle2 size={14} /> <b>{importResult.pack}</b> — imported {importResult.imported}, skipped {importResult.skipped}
                  </div>
                )}
                <div className="vj-preset-grid">
                  {presetPacks.length === 0 && <p className="hint">Loading packs…</p>}
                  {presetPacks.map((p) => (
                    <div key={p.id} className="vj-preset-card" data-testid={`vj-preset-${p.id}`}>
                      <div className="vj-preset-head">
                        <b><span style={{ fontSize: 18, marginRight: 6 }}>{p.emoji}</span>{p.name}</b>
                        <small>{p.templates.length} templates</small>
                      </div>
                      <p className="hint">{p.description}</p>
                      <ul className="vj-preset-preview">
                        {p.templates.slice(0, 3).map((t, i) => (
                          <li key={i}>{t.category} — {t.text}</li>
                        ))}
                        {p.templates.length > 3 && <li className="more">+ {p.templates.length - 3} more…</li>}
                      </ul>
                      <button className="primary" data-testid={`vj-preset-import-${p.id}`} disabled={importingPack === p.id} onClick={() => importPack(p)}>
                        <Package size={12} /> {importingPack === p.id ? "Importing…" : "Import pack"}
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

/* ----------------- sub-components ----------------- */

function StatTile({ icon: Icon, label, value, suffix, tone, testid }) {
  return (
    <div className={`vj-stat ${tone}`} data-testid={testid}>
      <Icon size={14} />
      <span className="vj-stat-label">{label}</span>
      <b>{value}<em>{suffix || ""}</em></b>
    </div>
  );
}

function TimelineTab({ groupedTimeline, typeFilter, setTypeFilter, q, setQ, typeCounts }) {
  const chips = [
    { id: "all", label: "All" },
    ...Object.keys(TYPE_META).map((k) => ({ id: k, label: TYPE_META[k].label, count: typeCounts[k] || 0 })),
  ];
  return (
    <>
      <div className="vj-filters">
        <div className="vj-search"><Search size={13} /><input placeholder="Search timeline…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="vj-timeline-search" /></div>
        <div className="vj-chips">
          {chips.map((c) => (
            <button key={c.id} data-testid={`vj-chip-${c.id}`}
              className={`vj-chip ${typeFilter === c.id ? "on" : ""} ${c.count === 0 && c.id !== "all" ? "empty" : ""}`}
              onClick={() => setTypeFilter(c.id)}>
              {c.label}{typeof c.count === "number" && c.count > 0 ? <em>{c.count}</em> : null}
            </button>
          ))}
        </div>
      </div>

      {groupedTimeline.length === 0 ? (
        <div className="vj-empty" data-testid="vj-timeline-empty">
          <p><b>No touchpoints match your filter.</b></p>
          <p className="hint">Try clearing the filter or search.</p>
        </div>
      ) : (
        <div className="vj-timeline" data-testid="vj-timeline">
          {groupedTimeline.map(([month, items]) => (
            <div key={month} className="vj-month">
              <h3>{month === "no-date" ? "Undated" : formatMonth(month)}</h3>
              {items.map((t, i) => {
                const meta = TYPE_META[t.type] || { icon: Sparkles, tone: "blue" };
                const Icon = meta.icon;
                return (
                  <div key={i} className={`vj-item ${meta.tone}`} data-testid={`vj-item-${t.type}-${i}`}>
                    <div className="vj-item-icon"><Icon size={13} /></div>
                    <div className="vj-item-body">
                      <b>{t.title}</b>
                      <small>{t.meta}</small>
                      <small className="vj-when">{t.when ? formatWhen(t.when) : ""}</small>
                    </div>
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      )}
    </>
  );
}

const TPL_CATEGORIES = ["General", "Event", "Complaint", "Sentiment", "Follow-up"];
const TPL_CAT_COLOR = {
  General:   { bg: "#e6efe9", fg: "#35655d", border: "#dbe7df" },
  Event:     { bg: "#fbf1da", fg: "#a06508", border: "#f1e2b7" },
  Complaint: { bg: "#f9dedb", fg: "#a13c31", border: "#f0c4bf" },
  Sentiment: { bg: "#e9e4f5", fg: "#4a2ea0", border: "#dcd4ee" },
  "Follow-up": { bg: "#d7edf5", fg: "#0e5b73", border: "#bfe0eb" },
};

function NotesTab({ notes, isAdmin, user, noteText, setNoteText, onAdd, onDelete, busy, err, templates = [], onDropInTemplate, onOpenManageTpl }) {
  // Group templates by category (preserve creation order within a group)
  const groups = templates.reduce((acc, t) => {
    const cat = t.category || "General";
    (acc[cat] = acc[cat] || []).push(t);
    return acc;
  }, {});
  const catOrder = TPL_CATEGORIES.filter((c) => groups[c]).concat(Object.keys(groups).filter((c) => !TPL_CATEGORIES.includes(c)));
  return (
    <>
      {isAdmin && (
        <>
          {templates.length > 0 && (
            <div className="vj-tpl-strip" data-testid="vj-tpl-strip">
              {catOrder.map((cat) => {
                const c = TPL_CAT_COLOR[cat] || TPL_CAT_COLOR.General;
                return (
                  <div key={cat} className="vj-tpl-group" data-testid={`vj-tpl-group-${cat.toLowerCase()}`}>
                    <span className="vj-tpl-cat" style={{ background: c.bg, color: c.fg, borderColor: c.border }}>{cat}</span>
                    {groups[cat].map((t) => (
                      <button key={t.template_id} className="vj-tpl-chip" data-testid={`vj-tpl-${t.template_id}`}
                        style={{ borderColor: c.border, color: c.fg }}
                        onClick={() => onDropInTemplate(t)} title={`${t.text}\n\nUsed ${t.use_count || 0} times`}>
                        {t.text.length > 40 ? `${t.text.slice(0, 40)}…` : t.text}
                        {t.use_count > 0 && (
                          <em className="vj-tpl-uses" data-testid={`vj-tpl-uses-${t.template_id}`}> · {t.use_count} use{t.use_count === 1 ? "" : "s"}</em>
                        )}
                      </button>
                    ))}
                  </div>
                );
              })}
              <button className="vj-tpl-manage" data-testid="vj-tpl-manage" onClick={onOpenManageTpl}><Plus size={11} /> Manage</button>
            </div>
          )}
          {templates.length === 0 && (
            <button className="vj-tpl-manage vj-tpl-manage--empty" data-testid="vj-tpl-manage" onClick={onOpenManageTpl}>
              <Plus size={11} /> Create quick-drop templates
            </button>
          )}
          <div className="vj-note-composer" data-testid="vj-note-composer">
            <textarea rows={2} placeholder="Add an internal observation about this voter (visible only to staff)…"
              value={noteText} onChange={(e) => setNoteText(e.target.value)} data-testid="vj-note-input" />
            <button className="primary" disabled={busy || !noteText.trim()} onClick={onAdd} data-testid="vj-note-add"><Send size={14} /> Save note</button>
          </div>
        </>
      )}
      {err && <p className="hint" style={{ color: "#b91c1c" }}>{err}</p>}

      {notes.length === 0 ? (
        <div className="vj-empty" data-testid="vj-notes-empty">
          <p><StickyNote size={22} /></p>
          <p><b>No notes yet.</b></p>
          <p className="hint">{isAdmin ? "Log observations, phone-call outcomes, promises made." : "Only admins can post notes."}</p>
        </div>
      ) : (
        <ul className="vj-notes" data-testid="vj-notes-list">
          {notes.map((n) => (
            <NoteRow key={n.note_id} note={n} user={user} isAdmin={isAdmin} onDelete={onDelete} />
          ))}
        </ul>
      )}
    </>
  );
}

function NoteRow({ note, user, isAdmin, onDelete }) {
  const [n, setN] = useState(note);
  const [showBn, setShowBn] = useState(false);
  const [busy, setBusy] = useState(false);
  const canDelete = user && (n.author_id === user.user_id || user.role === "Super Admin");
  const hasBn = !!(n.text_bn && n.text_bn.trim());
  const hasEn = !!((n.text_en || (n.lang === "en" ? n.text : "")) || "").trim();
  const isBnOnly = n.lang === "bn" && !hasEn;
  const translate = async () => {
    if (!hasEn) return;
    setBusy(true);
    try {
      const r = await http.post(`/voters/${n.contact_id}/notes/${n.note_id}/translate`);
      setN({ ...n, text_bn: r.data.text_bn, translated_at: r.data.translated_at });
      setShowBn(true);
    } catch (e) { window.alert(e.response?.data?.detail || "Translation failed"); }
    finally { setBusy(false); }
  };
  const displayText = showBn && hasBn ? n.text_bn : (n.text_en || n.text);
  return (
    <li className="vj-note" data-testid={`vj-note-${n.note_id}`}>
      <div className="vj-note-head">
        <div className="vj-note-author">
          {n.author_picture
            ? <img src={n.author_picture.startsWith("http") ? n.author_picture : `${API}/files/${n.author_picture}`} alt={n.author_name} />
            : <span>{(n.author_name || "?").split(" ").map((s) => s[0]).join("").slice(0, 2)}</span>}
          <div><b>{n.author_name}</b><small>{formatWhen(n.created_at)}</small></div>
        </div>
        <div style={{ display: "flex", gap: 4, alignItems: "center" }}>
          {isAdmin && !isBnOnly && (
            hasBn ? (
              <button className="vj-note-lang" data-testid={`vj-note-lang-${n.note_id}`} onClick={() => setShowBn((s) => !s)} title="Toggle EN / বাংলা">
                <Languages size={11} /> {showBn ? "EN" : "বাংলা"}
              </button>
            ) : (
              <button className="vj-note-lang" data-testid={`vj-note-translate-${n.note_id}`} disabled={busy} onClick={translate} title="Auto-translate to Bengali">
                <Languages size={11} /> {busy ? "..." : "→ বাংলা"}
              </button>
            )
          )}
          {canDelete && <button className="icon-btn danger" onClick={() => onDelete(n.note_id)} data-testid={`vj-note-del-${n.note_id}`} title="Delete note"><Trash2 size={13} /></button>}
        </div>
      </div>
      <p>{displayText}</p>
    </li>
  );
}

function GraphTab({ graph, loading, centerId }) {
  if (loading || !graph) return <p className="hint" style={{ padding: 30, textAlign: "center" }}>Loading network…</p>;
  const nodes = graph.nodes || [];
  const edges = graph.edges || [];
  if (nodes.length <= 1) return (
    <div className="vj-empty" data-testid="vj-graph-empty">
      <Network size={22} />
      <p><b>No connections mapped yet.</b></p>
      <p className="hint">Family, organisation members and team-role peers will appear here as you tag them.</p>
    </div>
  );
  // Radial layout — self in the center, everyone else on a circle
  const cx = 300, cy = 260, r = 190;
  const peers = nodes.filter((n) => n.id !== centerId);
  const positions = new Map();
  positions.set(centerId, { x: cx, y: cy });
  peers.forEach((n, i) => {
    const angle = (2 * Math.PI * i) / peers.length - Math.PI / 2;
    positions.set(n.id, { x: cx + r * Math.cos(angle), y: cy + r * Math.sin(angle) });
  });
  const kindColor = { household: "#65aa79", organization: "#7a4bb6", volunteer: "#0f766e" };
  const groupBadge = { self: "#0f766e", household: "#65aa79", organization: "#7a4bb6", volunteer: "#efb64e" };

  return (
    <div className="vj-graph" data-testid="vj-graph">
      <svg viewBox="0 0 600 520" width="100%" height="520">
        {edges.map((e, i) => {
          const a = positions.get(e.source); const b = positions.get(e.target);
          if (!a || !b) return null;
          return <line key={i} x1={a.x} y1={a.y} x2={b.x} y2={b.y} stroke={kindColor[e.kind] || "#dbe7df"} strokeWidth="1.4" strokeDasharray={e.kind === "volunteer" ? "3 3" : ""} data-testid={`vj-edge-${i}`} />;
        })}
        {nodes.map((n) => {
          const p = positions.get(n.id);
          if (!p) return null;
          const isSelf = n.id === centerId;
          return (
            <g key={n.id} data-testid={`vj-node-${n.group}-${n.id}`}>
              <circle cx={p.x} cy={p.y} r={isSelf ? 34 : 22} fill="white" stroke={groupBadge[n.group] || "#0f766e"} strokeWidth={isSelf ? 3 : 2} />
              <text x={p.x} y={p.y + 4} textAnchor="middle" fontSize={isSelf ? 12 : 10} fontWeight="700" fill="#102a2b">
                {(n.name || "?").split(" ").map((s) => s[0]).join("").slice(0, 2).toUpperCase()}
              </text>
              <text x={p.x} y={p.y + (isSelf ? 50 : 38)} textAnchor="middle" fontSize="10" fill="#35655d">{shorten(n.name, 18)}</text>
              {n.sub && <text x={p.x} y={p.y + (isSelf ? 63 : 50)} textAnchor="middle" fontSize="9" fill="#7a8a80">{shorten(n.sub, 20)}</text>}
            </g>
          );
        })}
      </svg>
      <div className="vj-graph-legend">
        <span><i style={{ background: "#65aa79" }} /> Household</span>
        <span><i style={{ background: "#7a4bb6" }} /> Organisation</span>
        <span><i style={{ background: "#0f766e", borderStyle: "dashed" }} /> Team role</span>
      </div>
    </div>
  );
}

/* ---------------- utilities ---------------- */
function TemplateAnalyticsView({ data, loading }) {
  if (loading || !data) {
    return <p className="hint" style={{ textAlign: "center", padding: 24 }} data-testid="vj-tpl-analytics-loading">Loading usage analytics…</p>;
  }
  const totals = data.totals || {};
  const trending = data.trending || [];
  const rows = data.templates || [];
  // Build month keys for the rollup header (last N months, ascending)
  const monthKeys = [];
  const monthSet = new Set();
  rows.forEach((r) => Object.keys(r.monthly || {}).forEach((m) => monthSet.add(m)));
  Array.from(monthSet).sort().forEach((m) => monthKeys.push(m));
  const maxMonthly = Math.max(1, ...rows.flatMap((r) => Object.values(r.monthly || {})));

  return (
    <div className="vj-tpl-analytics" data-testid="vj-tpl-analytics">
      <div className="vj-tpl-analytics-totals" data-testid="vj-tpl-analytics-totals">
        <div><em>Templates</em><b>{totals.templates || 0}</b></div>
        <div><em>All-time uses</em><b>{totals.total_hits || 0}</b></div>
        <div><em>Last 30 days</em><b>{totals.hits_last_30d || 0}</b></div>
        <div><em>This month</em><b>{totals.this_month || 0}</b></div>
      </div>

      {trending.length > 0 && (
        <div className="vj-tpl-trending" data-testid="vj-tpl-trending">
          <h4><Flame size={12} /> Trending this month</h4>
          <ul>
            {trending.map((t, i) => {
              const c = TPL_CAT_COLOR[t.category] || TPL_CAT_COLOR.General;
              return (
                <li key={t.template_id} data-testid={`vj-tpl-trending-${i}`}>
                  <span className="vj-tpl-trend-rank">{i + 1}</span>
                  <span className="vj-tpl-cat" style={{ background: c.bg, color: c.fg, borderColor: c.border }}>{t.category}</span>
                  <span className="vj-tpl-trend-text">{t.text}</span>
                  <b>{t.monthly[data.this_month] || 0} this month</b>
                </li>
              );
            })}
          </ul>
        </div>
      )}

      <div className="vj-tpl-rollup" data-testid="vj-tpl-rollup">
        <h4>Monthly rollup</h4>
        {rows.length === 0 ? (
          <p className="hint">No templates yet.</p>
        ) : monthKeys.length === 0 ? (
          <p className="hint">No template drops recorded in the last {data.months} months.</p>
        ) : (
          <div className="vj-tpl-rollup-scroll">
            <table>
              <thead>
                <tr>
                  <th>Template</th>
                  {monthKeys.map((m) => <th key={m} title={m}>{formatMonthShort(m)}</th>)}
                  <th style={{ textAlign: "right" }}>Total</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => {
                  const c = TPL_CAT_COLOR[r.category] || TPL_CAT_COLOR.General;
                  return (
                    <tr key={r.template_id} data-testid={`vj-tpl-rollup-row-${r.template_id}`}>
                      <td>
                        <span className="vj-tpl-cat" style={{ background: c.bg, color: c.fg, borderColor: c.border, marginRight: 6 }}>{r.category}</span>
                        <span title={r.text}>{r.text.length > 42 ? `${r.text.slice(0, 42)}…` : r.text}</span>
                      </td>
                      {monthKeys.map((m) => {
                        const v = r.monthly?.[m] || 0;
                        const w = Math.round((v / maxMonthly) * 100);
                        return (
                          <td key={m} className="vj-tpl-rollup-cell">
                            <div className="vj-tpl-rollup-bar" title={`${v} drops in ${m}`}>
                              <span style={{ width: `${w}%`, opacity: v ? 1 : 0 }} />
                              <em>{v || ""}</em>
                            </div>
                          </td>
                        );
                      })}
                      <td style={{ textAlign: "right", fontWeight: 700 }}>{r.use_count}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function formatMonthShort(ym) {
  try {
    const d = new Date(`${ym}-01T00:00:00Z`);
    return d.toLocaleDateString(undefined, { month: "short", year: "2-digit" });
  } catch { return ym; }
}

function shorten(s, n) { if (!s) return ""; return s.length > n ? s.slice(0, n - 1) + "…" : s; }
function formatMonth(ym) {
  try {
    const d = new Date(`${ym}-01T00:00:00Z`);
    return d.toLocaleDateString(undefined, { month: "long", year: "numeric" });
  } catch { return ym; }
}
function downloadJourneyQr(name, contactId) {
  const svg = document.querySelector('[data-testid="vj-qr-svg"] svg');
  if (!svg) return;
  const xml = new XMLSerializer().serializeToString(svg);
  const svg64 = btoa(unescape(encodeURIComponent(xml)));
  const img = new Image();
  img.onload = () => {
    const canvas = document.createElement("canvas");
    canvas.width = 512; canvas.height = 512;
    const ctx = canvas.getContext("2d");
    ctx.fillStyle = "#ffffff"; ctx.fillRect(0, 0, 512, 512);
    ctx.drawImage(img, 0, 0, 512, 512);
    const link = document.createElement("a");
    link.download = `${(name || "voter").replace(/\s+/g, "-").toLowerCase()}-${contactId}.png`;
    link.href = canvas.toDataURL("image/png");
    link.click();
  };
  img.src = `data:image/svg+xml;base64,${svg64}`;
}
function formatWhen(iso) {
  try {
    const d = new Date(iso);
    const now = new Date();
    const diff = now.getTime() - d.getTime();
    const mins = Math.round(diff / 60000);
    if (mins < 1) return "just now";
    if (mins < 60) return `${mins} min ago`;
    const hrs = Math.round(mins / 60);
    if (hrs < 24) return `${hrs} hr ago`;
    const days = Math.round(hrs / 24);
    if (days < 8) return `${days} day${days === 1 ? "" : "s"} ago`;
    return d.toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });
  } catch { return iso; }
}
